import React, { Suspense, useEffect, useState } from 'react';
import { IOrder, IOrderDriver } from '@/interfaces/order';
import {
  Button,
  Col,
  Divider,
  notification,
  Rate,
  Row,
  Space,
  TableProps,
} from 'antd';
import { formatDate } from '@/utils/dataUtils';
import TableComponent from '@/components/table';
import Title from '@/components/title';

import { FrownOutlined, MehOutlined, SmileOutlined } from '@ant-design/icons';
import { dateTimeFormat } from '@/configs/dateFormat';
import dayjs from 'dayjs';
import PlaceHolder from '@/images/product-placeholder.jpg';
import { mediaUrl } from '@/utils/mediaUtils';
import { useMutation } from '@tanstack/react-query';
import { syncDelievry } from '@/adapters/inbound';
import PrintModal from '@/components/templates/print-modal';
import { patchUpdateDriver } from '@/adapters/order';
import { twMerge } from 'tailwind-merge';

interface IProps {
  data: IOrder;
  onActioned: () => void;
}

const customIcons: Record<number, React.ReactNode> = {
  1: <FrownOutlined />,
  2: <FrownOutlined />,
  3: <MehOutlined />,
  4: <SmileOutlined />,
  5: <SmileOutlined />,
};

const DeliveryDriver = ({ data, onActioned }: IProps) => {
  const [dataSource, setDataSource] = useState<IOrderDriver[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>('');
  const [print, setPrint] = useState<{ open: boolean; driverId: number }>({
    open: false,
    driverId: 0,
  });
  const columns: TableProps<IOrderDriver>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'ทะเบียนรถ',
      dataIndex: 'tabian',
      key: 'tabian',
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'ชื่อคนขับ',
      dataIndex: 'driverName',
      key: 'driverName',
      className: 'font-bold',
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'driverPhoneNumber',
      key: 'driverPhoneNumber',
      className: 'font-bold',
    },
    {
      title: 'CHECK IN',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      className: 'font-bold',
      align: 'center',
      render: _ => (_ && formatDate(_, dateTimeFormat)) || '',
    },
    {
      title: 'ON DELIVERY',
      dataIndex: 'onDeliveryDate',
      key: 'onDeliveryDate',
      className: 'font-bold',
      align: 'center',
      render: _ => (_ && formatDate(_, dateTimeFormat)) || '',
    },
    {
      title: 'CHECK OUT',
      dataIndex: 'checkOutDate',
      key: 'checkOutDate',
      className: 'font-bold',
      align: 'center',
      render: _ => (_ && formatDate(_, dateTimeFormat)) || '',
    },
    {
      title: 'DELIVERYED',
      dataIndex: 'deliveryedDate',
      key: 'deliveryedDate',
      className: 'font-bold',
      align: 'center',
      render: _ => (_ && formatDate(_, dateTimeFormat)) || '',
    },
    {
      title: 'Hours',
      dataIndex: 'deliveryedDate',
      key: 'deliveryedDate',
      className: 'font-bold',
      align: 'center',
      render: (_, rec) => {
        if (rec.deliveryedDate && rec.checkInDate) {
          const start = dayjs(rec.checkInDate);
          const end = dayjs(rec.deliveryedDate);
          const diffInMinutes = end.diff(start, 'minute');
          const hours = Math.floor(diffInMinutes / 60);
          const minutes = diffInMinutes % 60;
          return `${hours}h ${minutes}m`;
        }
        return '';
      },
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      align: 'center',
      width: 180,
      render: _ => (
        <div className="items-center justify-center flex">
          <Rate
            character={({ index = 0 }) => customIcons[index + 1]}
            style={{ fontSize: 24 }}
            className="text-black"
            defaultValue={_}
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      render: (_, rec) => (
        <div className="flex flex-row gap-1">
          <Button
            type="primary"
            onClick={() =>
              toggleDriverStatusMutation.mutateAsync({
                id: _,
                isActive: !rec.isActive,
              })
            }
            className={twMerge('bg-green-600', !rec.isActive && 'bg-red-600')}
            loading={toggleDriverStatusMutation.isPending}
          >
            {rec.isActive ? 'Active' : 'In Active'}
          </Button>
          <Button
            type="primary"
            onClick={() => setPrint({ driverId: _, open: true })}
            ghost
          >
            พิมพ์ PackingList
          </Button>
        </div>
      ),
    },
  ];

  const fetchDataWithImageUrls = async () => {
    // Map deliveryImageIds into resolved URLs
    return Promise.all(
      data.orderDrivers.map(async record => {
        const imageIds = (record.receivedImageIds || '')
          .split(',')
          .map(Number)
          .filter(i => i !== 0);
        const checkoutImages = (record.deliveryImageIds || '')
          .split(',')
          .map(Number)
          .filter(i => i !== 0);
        const docImages = (record.doImageIds || '')
          .split(',')
          .map(Number)
          .filter(i => i !== 0);
        const signImages = (record.signatureImageIds || '')
          .split(',')
          .map(Number)
          .filter(i => i !== 0);
        const checkinUrls = await Promise.all(
          imageIds.map(id => mediaUrl({ id })),
        );
        const checkoutUrls = await Promise.all(
          checkoutImages.map(id => mediaUrl({ id })),
        );
        const docUrls = await Promise.all(
          docImages.map(id => mediaUrl({ id })),
        );
        const signUrls = await Promise.all(
          signImages.map(id => mediaUrl({ id })),
        );
        return {
          ...record,
          checkinImages: checkinUrls,
          checkoutImages: checkoutUrls,
          docImages: docUrls,
          signImages: signUrls,
        };
      }),
    );
  };

  useEffect(() => {
    const load = async () => {
      const dataWithUrls = await fetchDataWithImageUrls();
      setDataSource(dataWithUrls);
    };
    load();
  }, [data]);

  const syncDeliverys = useMutation({
    mutationFn: async () => await syncDelievry(),
    mutationKey: ['calculate-cost'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'sync delivery success',
        });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const toggleDriverStatusMutation = useMutation({
    mutationFn: async (data: Partial<IOrderDriver>) =>
      await patchUpdateDriver(data),
    mutationKey: ['patch-driver-update'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'อัพเดทสถานะคนขับสำเร็จ',
        });
        onActioned();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  return (
    <Row gutter={12} className="">
      <Col span={24}>
        <div className="border-2 border-dashed rounded-md px-3 pb-4">
          <Title title="คนขับรถ">
            <Button
              type="primary"
              ghost
              loading={syncDeliverys.isPending}
              onClick={async () => syncDeliverys.mutateAsync()}
            >
              SYNC DELIVERY
            </Button>
          </Title>
          <TableComponent<IOrderDriver>
            columns={columns}
            data={{
              data: dataSource || [],
              totalRecords: 0,
            }}
            loading={false}
            showPagination={false}
            rowKey="id"
            scroll
            expandable={{
              expandedRowRender: record => (
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row gap-3">
                    <div className="flex flex-col items-center">
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {record.checkinImages?.map(
                          (url: string, index: number) => (
                            <div
                              key={index}
                              className="h-[80px] flex items-center justify-center rounded-lg overflow-hidden bg-gray-100 cursor-pointer  shadow-md"
                              onClick={() => setSelectedImage(url)}
                            >
                              <img
                                src={url}
                                className="max-h-full max-w-full object-contain"
                                onError={e => {
                                  (e.currentTarget as HTMLImageElement).src =
                                    PlaceHolder;
                                }}
                              />
                            </div>
                          ),
                        )}
                      </div>
                      <div>check in</div>
                    </div>
                    <Divider type="vertical" className="h-full" />
                    <div className="flex flex-col items-center">
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {record.checkoutImages?.map(
                          (url: string, index: number) => (
                            <div
                              key={index}
                              className="h-[80px]  flex items-center justify-center rounded-lg overflow-hidden bg-gray-100 cursor-pointer shadow-md"
                              onClick={() => setSelectedImage(url)}
                            >
                              <img
                                src={url}
                                className="max-h-full max-w-full object-contain"
                                onError={e => {
                                  (e.currentTarget as HTMLImageElement).src =
                                    PlaceHolder;
                                }}
                              />
                            </div>
                          ),
                        )}
                      </div>
                      <div>check out</div>
                    </div>
                    <Divider type="vertical" className="h-full" />
                    <div className="flex flex-col items-center">
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {record.docImages?.map((url: string, index: number) => (
                          <div
                            key={index}
                            className="h-[80px]  flex items-center justify-center rounded-lg overflow-hidden bg-gray-100 cursor-pointer shadow-md"
                            onClick={() => setSelectedImage(url)}
                          >
                            <img
                              src={url}
                              className="max-h-full max-w-full object-contain"
                              onError={e => {
                                (e.currentTarget as HTMLImageElement).src =
                                  PlaceHolder;
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <div>docs</div>
                    </div>
                    <Divider type="vertical" className="h-full" />
                    <div className="flex flex-col items-center">
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {record.signImages?.map(
                          (url: string, index: number) => (
                            <div
                              key={index}
                              className="h-[80px]  flex items-center justify-center rounded-lg overflow-hidden bg-gray-100 cursor-pointer shadow-md"
                              onClick={() => setSelectedImage(url)}
                            >
                              <img
                                src={url}
                                className="max-h-full max-w-full object-contain"
                                onError={e => {
                                  (e.currentTarget as HTMLImageElement).src =
                                    PlaceHolder;
                                }}
                              />
                            </div>
                          ),
                        )}
                      </div>
                      <div>signature</div>
                    </div>
                  </div>
                </div>
              ),
              expandedRowKeys: dataSource.map(item => item.id),
            }}
          />
        </div>
      </Col>
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white p-2 rounded shadow-lg max-w-[90vw] max-h-[90vh]"
            onClick={e => e.stopPropagation()} // prevent closing when clicking the image
          >
            <img
              src={selectedImage}
              alt="preview"
              className="max-w-full max-h-[80vh] object-contain"
            />
          </div>
        </div>
      )}
      <Suspense>
        <PrintModal
          open={print.open}
          handleCancel={() => setPrint({ driverId: 0, open: false })}
          tms={['TM27']}
          order={data}
          driverId={print.driverId}
        />
      </Suspense>
    </Row>
  );
};

export default DeliveryDriver;
